import React, { useMemo } from 'react'
import Link from 'next/link'

import { Button } from '@wartek-id/button-v1'
import type { ButtonProps } from '@wartek-id/button-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'

import {
  Table as KinerjaTable,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import { EmptyState } from 'app/KinerjaShared/components/PenetapanPredikatTable/EmptyState'
import {
  STATUS_PENGIRIMAN_LABEL,
  PERILAKU_KERJA_RATING_LABEL,
  PRAKTIK_KINERJA_RATING_LABEL,
} from 'app/KinerjaShared/constants/penetapanPredikatKinerja'
import { PredikatBadge } from 'app/KinerjaShared/components/PredikatBadge'
import { usePenetapanPredikatTableStore } from 'app/KinerjaShared/components/PenetapanPredikatTable/Provider'
import { ALL_COLUMNS } from 'app/KinerjaShared/components/PenetapanPredikatTable/constants'

import type { PenetapanPredikatKinerjaStoreItem } from 'app/KinerjaShared/stores/penetapan-predikat-kinerja-store'
import type { ColumnConfig } from 'app/KinerjaShared/components/PenetapanPredikatTable/type'

type TableProps = {
  isLoading?: boolean
  columns?: Array<ColumnConfig>
  generateDetailUrl?: (item: PenetapanPredikatKinerjaStoreItem) => string
  /* Generate custom action for each item. Return null to use the default action. */
  generateCustomAction?: GenerateCustomActionFn
}

type GenerateCustomActionFn = (
  item: PenetapanPredikatKinerjaStoreItem
) => React.ReactNode | null | undefined

export function Table({
  isLoading,
  columns = ALL_COLUMNS,
  generateDetailUrl,
  generateCustomAction,
}: TableProps) {
  const {
    visibleItems,
    selectedItems,
    selectItem,
    unselectItem,
    selectAllItems,
    unselectAllItems,
    isSelectableItem,
  } = usePenetapanPredikatTableStore((store) => ({
    visibleItems: store.visibleItems,
    selectedItems: store.selectedItems,
    selectItem: store.selectItem,
    unselectItem: store.unselectItem,
    selectAllItems: store.selectAllItems,
    unselectAllItems: store.unselectAllItems,
    isSelectableItem: store.isSelectableItem,
  }))

  const selectableItems = useMemo(
    () => visibleItems.filter(isSelectableItem),
    [visibleItems, isSelectableItem]
  )

  const isAllSelectableItemsSelected = useMemo(() => {
    if (selectableItems.length === 0) return false

    return selectableItems.every((item) => selectedItems.has(item.id))
  }, [selectableItems, selectedItems])

  const visibleColumns = useMemo(() => {
    return columns.filter((col) => col.visible)
  }, [columns])

  return (
    <KinerjaTable withOuterBorder rounded fullWidth className="bg-white">
      {/* colgroup is used to target the columns with CSS selectors */}
      <colgroup>
        {visibleColumns.map((column) => (
          <col data-column={column.key} key={column.key} />
        ))}
      </colgroup>

      <TableHeader className="!bg-blue-80 text-white">
        {visibleColumns.map((column) => {
          if (column.key === 'checkbox') {
            return (
              <TableHeaderItem key={column.key}>
                <div className="flex px-1">
                  <Checkbox
                    aria-label="Pilih semua pegawai"
                    indeterminate={
                      !isAllSelectableItemsSelected && selectedItems.size > 0
                    }
                    checked={isAllSelectableItemsSelected}
                    disabled={selectableItems.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        selectAllItems()
                      } else {
                        unselectAllItems()
                      }
                    }}
                  />
                </div>
              </TableHeaderItem>
            )
          }

          return (
            <TableHeaderItem
              key={column.key}
              className={`py-5 ${column.headerClassName || ''}`}
            >
              {column.label}
            </TableHeaderItem>
          )
        })}
      </TableHeader>

      <TableBody key={`table-body-${visibleItems.length}-${isLoading}`}>
        {isLoading && (
          <TableShimmer
            bordered={false}
            rows={7}
            columns={visibleColumns.length}
          />
        )}
        {!isLoading && (
          <>
            {visibleItems.length > 0 ? (
              visibleItems.map((item) => {
                const isSelected = selectedItems.has(item.id)
                const isDisabled = !isSelectableItem(item)

                return (
                  <TableRow
                    bordered={false}
                    key={`${item.id}-${item.name}`}
                    className="align-middle"
                  >
                    {visibleColumns.map((column) => {
                      if (column.key === 'checkbox') {
                        return (
                          <TableRowItem key={column.key}>
                            <div className="flex items-center px-1">
                              <Checkbox
                                aria-label={`Pilih ${item.name}`}
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    selectItem(item)
                                  } else {
                                    unselectItem(item.id)
                                  }
                                }}
                              />
                            </div>
                          </TableRowItem>
                        )
                      }

                      return (
                        <TableRowItem
                          key={column.key}
                          className={column.className}
                        >
                          <TableCellContent
                            column={column}
                            item={item}
                            url={generateDetailUrl?.(item)}
                            generateCustomAction={generateCustomAction}
                          />
                        </TableRowItem>
                      )
                    })}
                  </TableRow>
                )
              })
            ) : (
              <TableRow className="border-none">
                <TableRowItem colSpan={visibleColumns.length} className="p-9">
                  <EmptyState />
                </TableRowItem>
              </TableRow>
            )}
          </>
        )}
      </TableBody>
    </KinerjaTable>
  )
}

function TableCellContent({
  column,
  item,
  url,
  generateCustomAction,
}: {
  column: ColumnConfig
  item: PenetapanPredikatKinerjaStoreItem
  url?: string
  generateCustomAction?: GenerateCustomActionFn
}) {
  switch (column.key) {
    case 'name':
      return <>{item.name || '-'}</>
    case 'nip':
      return <>{item.nip || '-'}</>
    case 'jabatan':
      return <>{item.jabatan || '-'}</>
    case 'satuanPendidikan':
      return <>{item.satuanPendidikan || '-'}</>
    case 'praktikKinerja':
      return <>{PRAKTIK_KINERJA_RATING_LABEL[item.praktikKinerja] || '-'}</>
    case 'perilakuKerja':
      return <>{PERILAKU_KERJA_RATING_LABEL[item.perilakuKerja] || '-'}</>
    case 'predikatKinerja':
      return <PredikatBadge item={item} />
    case 'statusPengiriman':
      return <>{STATUS_PENGIRIMAN_LABEL[item.statusPengiriman]}</>
    case 'action':
      return (
        <ActionButton
          item={item}
          url={url}
          generateCustomAction={generateCustomAction}
        />
      )
    default:
      return null
  }
}

const ACTION_BUTTON_PROPS: Record<
  string,
  {
    label: string
    color: ButtonProps['color']
    enabled: boolean
  }
> = {
  UBAH_PENETAPAN: {
    label: 'Ubah',
    color: 'white',
    enabled: true,
  },
  LAKUKAN_PENETAPAN: {
    label: 'Lakukan',
    color: 'black',
    enabled: true,
  },
  LAKUKAN_PENETAPAN_DISABLED: {
    label: 'Lakukan',
    color: 'black',
    enabled: false,
  },
  CEK_RINCIAN: {
    label: 'Cek Rincian',
    color: 'white',
    enabled: true,
  },
}

function ActionButton({
  item,
  url,
  generateCustomAction,
}: {
  item: PenetapanPredikatKinerjaStoreItem
  url?: string
  generateCustomAction?: GenerateCustomActionFn
}) {
  const customAction = generateCustomAction?.(item)

  // Only render the custom action if its not null
  // Otherwise, render the default action
  if (React.isValidElement(customAction)) {
    return customAction
  }

  let buttonProps

  if (item.statusPengiriman === 'SENT') {
    buttonProps = ACTION_BUTTON_PROPS.CEK_RINCIAN
  } else if (item.predikat === 'PREDIKAT_NOT_SET') {
    buttonProps = ACTION_BUTTON_PROPS.LAKUKAN_PENETAPAN
  } else if (item.predikat === 'UNFINISHED_PENILAIAN') {
    buttonProps = ACTION_BUTTON_PROPS.LAKUKAN_PENETAPAN_DISABLED
  } else {
    buttonProps = ACTION_BUTTON_PROPS.UBAH_PENETAPAN
  }

  if (url) {
    return (
      <Link href={url} passHref>
        <Button
          size="sm"
          color={buttonProps.color}
          disabled={!buttonProps.enabled}
          className="mx-auto !inline-flex"
          as="a"
          type={undefined}
        >
          {buttonProps.label}
        </Button>
      </Link>
    )
  }

  return (
    <Button
      size="sm"
      color={buttonProps.color}
      disabled={!buttonProps.enabled}
      className="mx-auto"
    >
      {buttonProps.label}
    </Button>
  )
}
